import * as React from "react";
import type { TestLandingConfig } from "./config.js";

/**
 * Held-constant SEO content band for a test-landing page: the test's covered
 * topics, "how we help", "about the test" cards, and an FAQ — all authored in
 * each TestLandingConfig but, until now, rendered NOWHERE (the shipped LeanBody
 * template only draws the hero + bridge). This revives that dead content below
 * the fold, outside the fun/competition A/B stack so it's held constant across
 * arms and lands in the prerendered HTML. The FAQ here is what the FAQPage
 * JSON-LD (prerender.ts) points at, so the schema matches visible content.
 *
 * Every section is optional and renders only when the config provides it; the
 * whole band collapses to null when a config carries none (no empty chrome).
 * Styling reuses the existing `.tl-*` system (styles/test-prep-field.css).
 */
export function TestPrepInfo({ config }: { config: TestLandingConfig }): React.ReactElement | null {
  const { topics, help, flex, faq, sourceUrl, fullName } = config;
  if (!topics && !help && !flex && !faq) return null;

  const hid = (s: string) => `tpi-${config.slug}-${s}`;

  return (
    <div className="tl-info">
      <div className="container">
        {topics && (
          <section className="tl-info__sec" aria-labelledby={hid("topics")}>
            <span className="v-field__sec-eyebrow">{topics.eyebrow}</span>
            <h2 id={hid("topics")} className="v-field__sec-title">
              {topics.title}
            </h2>
            {topics.lede && <p className="tl-info__lede">{topics.lede}</p>}
            <div className="tl-grid tl-grid--topics">
              {topics.groups.map((g) => (
                <div key={g.label} className="tl-card tl-card--topic">
                  <h3 className="tl-card__title">{g.label}</h3>
                  <ul className="tl-list">
                    {g.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {help && (
          <section className="tl-info__sec" aria-labelledby={hid("help")}>
            <span className="v-field__sec-eyebrow">{help.eyebrow}</span>
            <h2 id={hid("help")} className="v-field__sec-title">
              {help.title}
            </h2>
            <div className="tl-grid tl-grid--help">
              {help.points.map((point, i) => (
                <div key={point} className="tl-card tl-card--help">
                  <span className="tl-card__num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <p>{point}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {flex && (
          <section className="tl-info__sec" aria-labelledby={hid("flex")}>
            <span className="v-field__sec-eyebrow">{flex.eyebrow}</span>
            <h2 id={hid("flex")} className="v-field__sec-title">
              {flex.title}
            </h2>
            {flex.lede && <p className="tl-info__lede">{flex.lede}</p>}
            <div className="tl-grid tl-grid--flex">
              {flex.cards.map((c) => (
                <div key={c.title} className="tl-card tl-card--flex">
                  <h3 className="tl-card__title">{c.title}</h3>
                  <p>{c.body}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {faq && (
          <section className="tl-info__sec" aria-labelledby={hid("faq")}>
            <span className="v-field__sec-eyebrow">{faq.eyebrow}</span>
            <h2 id={hid("faq")} className="v-field__sec-title">
              {faq.title}
            </h2>
            <div className="tl-faq">
              {faq.items.map((item) => (
                <div key={item.q} className="tl-faq__item">
                  <h3 className="tl-faq__q">{item.q}</h3>
                  <p className="tl-faq__a">{item.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {sourceUrl && (
          <p className="tl-source">
            Source:{" "}
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer nofollow">
              {fullName} — official test information
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
